import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  ToggleButtonGroup,
  ToggleButton,
  Fab,
  Zoom,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Fade,
  Divider,
  Stack,
} from '@mui/material';
import {
  TableRestaurant as TableIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MergeType as MergeIcon,
  ArrowForward as ArrowIcon,
  Restaurant as RestaurantIcon,
  CleaningServices as CleanIcon,
  EventSeat as SeatIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
  CheckCircle as FreeIcon,
  ExitToApp as CheckoutIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Layers as LayersIcon,
  CheckCircle as ReadyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const TABLE_STATUS = {
  empty: { label: 'خالی', color: '#4caf50', bg: '#e8f5e9', icon: <SeatIcon />, order: 0 },
  occupied: { label: 'مشغول', color: '#ff9800', bg: '#fff3e0', icon: <RestaurantIcon />, order: 1 },
  reserved: { label: 'ریزروڈ', color: '#2196f3', bg: '#e3f2fd', icon: <ReceiptIcon />, order: 2 },
  billing: { label: 'بلنگ', color: '#f44336', bg: '#ffebee', icon: <ReceiptIcon />, order: 3 },
  cleaning: { label: 'صفائی', color: '#9c27b0', bg: '#f3e5f5', icon: <CleanIcon />, order: 4 },
  merged: { label: 'مرج', color: '#607d8b', bg: '#eceff1', icon: <MergeIcon />, order: 5 },
};

const FLOORS = ['Ground Floor', 'First Floor', 'Second Floor', 'Rooftop', 'Terrace'];

let storageInstance = null;

async function getStorage() {
  if (storageInstance) return storageInstance;
  try {
    const module = await import('../database/storage');
    storageInstance = module.default || module;
    await storageInstance.init?.();
    return storageInstance;
  } catch (e) {
    console.warn('[TableManagement] Storage not available:', e);
    return null;
  }
}

const TableManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isElectron, setIsElectron] = useState(false);
  const [dbReady, setDbReady] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [selectedTable, setSelectedTable] = useState(null);
  const [formData, setFormData] = useState({
    table_number: '',
    floor: 'Ground Floor',
    capacity: 4,
    position_x: 0,
    position_y: 0,
  });

  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [selectedTablesForMerge, setSelectedTablesForMerge] = useState([]);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState(null);

  const [freeTableDialogOpen, setFreeTableDialogOpen] = useState(false);
  const [tableToFree, setTableToFree] = useState(null);
  const [freeLoading, setFreeLoading] = useState(false);

  const [cancelReserveDialogOpen, setCancelReserveDialogOpen] = useState(false);
  const [tableToCancelReserve, setTableToCancelReserve] = useState(null);

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionTable, setActionTable] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ==================== CHECK ELECTRON MODE ====================
  useEffect(() => {
    const isElectronMode = typeof window !== 'undefined' && 
      window.electronAPI && 
      window.electronAPI.isElectron === true;
    setIsElectron(isElectronMode);
  }, []);

  // ==================== CHECK DATABASE STATUS (ELECTRON ONLY) ====================
  useEffect(() => {
    if (!isElectron) {
      setDbReady(true);
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    const checkDbStatus = async () => {
      try {
        console.log('[TableManagement] Checking DB status...');
        
        if (!window.electronAPI?.dbStatus) {
          console.warn('[TableManagement] dbStatus method not available');
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkDbStatus, 1000);
          } else {
            setDbReady(true); // Fallback - try to load anyway
          }
          return;
        }

        const status = await window.electronAPI.dbStatus();
        console.log('[TableManagement] DB Status:', status);

        if (status.initialized && status.isWorking) {
          setDbReady(true);
          console.log('[TableManagement] ✅ Database ready');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          console.log(`⏳ DB not ready, retrying... (${attempts}/${maxAttempts})`);
          setTimeout(checkDbStatus, 1000);
        } else {
          console.warn('[TableManagement] DB not ready after max attempts, proceeding anyway');
          setDbReady(true);
        }
      } catch (error) {
        console.error('[TableManagement] DB status check error:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkDbStatus, 1000);
        } else {
          setDbReady(true);
        }
      }
    };

    checkDbStatus();
  }, [isElectron]);

  // ==================== LOAD TABLES ====================
  const loadTables = useCallback(async () => {
    if (!dbReady) {
      console.log('[TableManagement] Waiting for DB to be ready...');
      return;
    }

    try {
      setLoading(true);
      
      // ✅ ELECTRON MODE
      if (isElectron && window.electronAPI?.getTables) {
        console.log('[TableManagement] Loading tables via Electron...');
        const data = await window.electronAPI.getTables();
        console.log('[TableManagement] Tables loaded:', data?.length || 0);
        setTables(data || []);
        filterTables(data || [], selectedFloor);
        setLoading(false);
        return;
      }

      // ✅ STORAGE MODE
      const storage = await getStorage();
      if (storage && storage.getTables) {
        console.log('[TableManagement] Loading tables via Storage...');
        const data = await storage.getTables();
        setTables(data || []);
        filterTables(data || [], selectedFloor);
        setLoading(false);
        return;
      }

      // ✅ MOCK MODE (Fallback)
      console.log('[TableManagement] Using mock data...');
      const mockTables = Array.from({ length: 12 }, (_, i) => ({
        id: i + 1,
        table_number: `T-${String(i + 1).padStart(2, '0')}`,
        floor: ['Ground Floor', 'First Floor', 'Second Floor'][i % 3],
        capacity: [2, 4, 4, 6, 8][i % 5],
        status: ['empty', 'empty', 'occupied', 'empty', 'reserved', 'cleaning'][i % 6],
        current_order_id: i % 3 === 0 ? Math.floor(Math.random() * 1000) + 100 : null,
        position_x: (i % 4) * 120 + 20,
        position_y: Math.floor(i / 4) * 120 + 20,
      }));
      setTables(mockTables);
      filterTables(mockTables, selectedFloor);

    } catch (err) {
      console.error('[TableManagement] Load error:', err);
      showMessage('❌ Tables load nahi ho saki!', 'error');
      setTables([]);
      filterTables([], selectedFloor);
    } finally {
      setLoading(false);
    }
  }, [selectedFloor, showMessage, isElectron, dbReady]);

  // ==================== FILTER TABLES ====================
  const filterTables = (data, floor) => {
    if (floor === 'all') {
      setFilteredTables(data);
    } else {
      setFilteredTables(data.filter((t) => t.floor === floor));
    }
  };

  // ==================== EFFECTS ====================
  useEffect(() => {
    if (dbReady) {
      loadTables();
    }
  }, [loadTables, dbReady]);

  useEffect(() => {
    filterTables(tables, selectedFloor);
  }, [selectedFloor, tables]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!dbReady) return;
    const interval = setInterval(loadTables, 30000);
    return () => clearInterval(interval);
  }, [loadTables, dbReady]);

  // ==================== HANDLERS ====================
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTables();
    setTimeout(() => setRefreshing(false), 600);
  };

  const handleOpenAdd = () => {
    setDialogMode('add');
    setFormData({ table_number: '', floor: 'Ground Floor', capacity: 4, position_x: 0, position_y: 0 });
    setSelectedTable(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = (table, e) => {
    e?.stopPropagation();
    setDialogMode('edit');
    setFormData({
      table_number: table.table_number,
      floor: table.floor || 'Ground Floor',
      capacity: table.capacity || 4,
      position_x: table.position_x || 0,
      position_y: table.position_y || 0,
    });
    setSelectedTable(table);
    setOpenDialog(true);
  };

  const handleSaveTable = async () => {
    if (!formData.table_number.trim()) {
      showMessage('❌ Table number required!', 'error');
      return;
    }

    try {
      if (isElectron && window.electronAPI) {
        if (dialogMode === 'add') {
          await window.electronAPI.addTable(formData);
        } else {
          await window.electronAPI.deleteTable(selectedTable.id);
          await window.electronAPI.addTable(formData);
        }
        showMessage('✅ Table saved!', 'success');
        setOpenDialog(false);
        await loadTables();
        return;
      }

      const storage = await getStorage();
      if (storage) {
        if (dialogMode === 'add') {
          await storage.addTable({
            ...formData,
            status: 'empty',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        } else {
          await storage.deleteTable(selectedTable.id);
          await storage.addTable({
            ...formData,
            status: 'empty',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        }
        showMessage('✅ Table saved!', 'success');
        setOpenDialog(false);
        await loadTables();
        return;
      }

      throw new Error('No storage available');

    } catch (err) {
      console.error('[TableManagement] Save error:', err);
      showMessage(`❌ Save nahi ho saka: ${err.message}`, 'error');
    }
  };

  const handleDeleteClick = (table, e) => {
    e?.stopPropagation();
    setTableToDelete(table);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (isElectron && window.electronAPI?.deleteTable) {
        await window.electronAPI.deleteTable(tableToDelete.id);
        showMessage('✅ Table deleted!', 'success');
        setDeleteConfirmOpen(false);
        await loadTables();
        return;
      }

      const storage = await getStorage();
      if (storage?.deleteTable) {
        await storage.deleteTable(tableToDelete.id);
        showMessage('✅ Table deleted!', 'success');
        setDeleteConfirmOpen(false);
        await loadTables();
        return;
      }

      throw new Error('No storage available');

    } catch (err) {
      console.error('[TableManagement] Delete error:', err);
      showMessage(`❌ Delete nahi ho saka: ${err.message}`, 'error');
    }
  };

  const handleTableClick = (table) => {
    if (selectedTablesForMerge.length > 0) {
      toggleMergeSelection(table);
      return;
    }

    if (isMobile && table.status !== 'empty' && table.status !== 'cleaning') {
      setActionTable(table);
      setActionDialogOpen(true);
      return;
    }

    switch (table.status) {
      case 'empty':
        navigate('/billing', { state: { tableId: table.id, tableNumber: table.table_number, newOrder: true } });
        break;
      case 'occupied':
      case 'billing':
        navigate('/billing', { state: { tableId: table.id, tableNumber: table.table_number } });
        break;
      case 'reserved':
        setTableToCancelReserve(table);
        setCancelReserveDialogOpen(true);
        break;
      case 'cleaning':
        handleStatusChange(table.id, 'empty');
        break;
      case 'merged':
        navigate('/billing', { state: { tableId: table.id, tableNumber: table.table_number } });
        break;
      default:
        break;
    }
  };

  const handleStatusChange = async (tableId, newStatus) => {
    try {
      if (isElectron && window.electronAPI?.updateTableStatus) {
        await window.electronAPI.updateTableStatus(tableId, newStatus);
        showMessage(`✅ Table status: ${newStatus}`, 'success');
        await loadTables();
        return;
      }

      const storage = await getStorage();
      if (storage?.updateTableStatus) {
        await storage.updateTableStatus(tableId, newStatus);
        showMessage(`✅ Table status: ${newStatus}`, 'success');
        await loadTables();
        return;
      }

      throw new Error('No storage available');

    } catch (err) {
      console.error('[TableManagement] Status update error:', err);
      showMessage(`❌ Status update nahi ho saka: ${err.message}`, 'error');
    }
  };

  const handleFreeTable = (table, e) => {
    e?.stopPropagation();
    setTableToFree(table);
    setFreeTableDialogOpen(true);
  };

  const handleCheckoutComplete = async (needsCleaning = true) => {
    if (!tableToFree) return;
    
    setFreeLoading(true);
    const newStatus = needsCleaning ? 'cleaning' : 'empty';
    
    try {
      await handleStatusChange(tableToFree.id, newStatus);
      showMessage(
        needsCleaning 
          ? `✅ Table ${tableToFree.table_number} cleaning ke liye bhej diya!` 
          : `✅ Table ${tableToFree.table_number} free ho gaya!`,
        'success'
      );
      setFreeTableDialogOpen(false);
      setTableToFree(null);
      await loadTables();
    } catch (err) {
      console.error('[TableManagement] Free table error:', err);
      showMessage(`❌ Error: ${err.message}`, 'error');
    } finally {
      setFreeLoading(false);
    }
  };

  const handleCancelReservation = async () => {
    if (!tableToCancelReserve) return;
    
    try {
      await handleStatusChange(tableToCancelReserve.id, 'empty');
      showMessage(`✅ Reservation cancelled for ${tableToCancelReserve.table_number}`, 'success');
      setCancelReserveDialogOpen(false);
      setTableToCancelReserve(null);
    } catch (err) {
      console.error('[TableManagement] Cancel reservation error:', err);
      showMessage(`❌ Error: ${err.message}`, 'error');
    }
  };

  const handleStartFromReserved = () => {
    if (!tableToCancelReserve) return;
    setCancelReserveDialogOpen(false);
    navigate('/billing', { 
      state: { 
        tableId: tableToCancelReserve.id, 
        tableNumber: tableToCancelReserve.table_number, 
        newOrder: true 
      } 
    });
  };

  const toggleMergeSelection = (table) => {
    if (selectedTablesForMerge.find((t) => t.id === table.id)) {
      setSelectedTablesForMerge(selectedTablesForMerge.filter((t) => t.id !== table.id));
    } else {
      if (selectedTablesForMerge.length < 4) {
        setSelectedTablesForMerge([...selectedTablesForMerge, table]);
      } else {
        showMessage('❌ Max 4 tables can be merged', 'warning');
      }
    }
  };

  const handleMerge = async () => {
    if (selectedTablesForMerge.length < 2) {
      showMessage('❌ Select at least 2 tables to merge', 'warning');
      return;
    }
    
    try {
      const ids = selectedTablesForMerge.map((t) => t.id);
      
      if (isElectron && window.electronAPI?.mergeTables) {
        await window.electronAPI.mergeTables(ids);
        showMessage('✅ Tables merged!', 'success');
        setMergeDialogOpen(false);
        setSelectedTablesForMerge([]);
        await loadTables();
        return;
      }

      const storage = await getStorage();
      if (storage?.mergeTables) {
        await storage.mergeTables(ids);
        showMessage('✅ Tables merged!', 'success');
        setMergeDialogOpen(false);
        setSelectedTablesForMerge([]);
        await loadTables();
        return;
      }

      throw new Error('No storage available');

    } catch (err) {
      console.error('[TableManagement] Merge error:', err);
      showMessage(`❌ Merge nahi ho saka: ${err.message}`, 'error');
    }
  };

  const getStatusCounts = () => {
    const counts = {};
    Object.keys(TABLE_STATUS).forEach((key) => {
      counts[key] = tables.filter((t) => t.status === key).length;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  // ==================== RENDER TABLE CARD ====================
  const renderTableCard = (table) => {
    const status = TABLE_STATUS[table.status] || TABLE_STATUS.empty;
    const isSelected = selectedTablesForMerge.find((t) => t.id === table.id);
    const isMergeMode = selectedTablesForMerge.length > 0;
    const statusColor = status.color;
    const statusBg = status.bg;

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={table.id}>
        <Fade in timeout={300}>
          <Card
            onClick={() => handleTableClick(table)}
            sx={{
              cursor: 'pointer',
              borderRadius: 4,
              border: `2.5px solid ${isSelected ? '#ff6b35' : statusColor}`,
              bgcolor: isSelected ? '#fff3e0' : statusBg,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'visible',
              height: '100%',
              minHeight: 200,
              '&:hover': {
                transform: 'translateY(-6px) scale(1.02)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              },
              '&::before': isSelected ? {
                content: '""',
                position: 'absolute',
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                borderRadius: 4,
                background: 'linear-gradient(45deg, #ff6b35, #ff8a65)',
                zIndex: -1,
                opacity: 0.3,
              } : {},
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      bgcolor: statusColor,
                      color: '#fff',
                      width: isMobile ? 44 : 52,
                      height: isMobile ? 44 : 52,
                      fontWeight: 800,
                      fontSize: isMobile ? '1rem' : '1.2rem',
                      boxShadow: `0 4px 12px ${statusColor}40`,
                      border: '2px solid rgba(255,255,255,0.2)',
                    }}
                  >
                    {table.table_number}
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6rem' }}>
                      {table.floor}
                    </Typography>
                    <Chip
                      size="small"
                      label={status.label}
                      sx={{
                        bgcolor: `${statusColor}20`,
                        color: statusColor,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 22,
                        mt: 0.3,
                      }}
                    />
                  </Box>
                </Box>

                {!isMergeMode && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {(table.status === 'occupied' || table.status === 'billing' || table.status === 'merged') && (
                      <Tooltip title="Checkout / Free">
                        <IconButton
                          size="small"
                          onClick={(e) => handleFreeTable(table, e)}
                          sx={{
                            bgcolor: '#f44336',
                            color: '#fff',
                            p: 0.7,
                            '&:hover': { bgcolor: '#d32f2f', transform: 'scale(1.1)' },
                            transition: 'all 0.2s',
                          }}
                        >
                          <FreeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {table.status === 'reserved' && (
                      <Tooltip title="Cancel Reservation">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTableToCancelReserve(table);
                            setCancelReserveDialogOpen(true);
                          }}
                          sx={{
                            bgcolor: '#f44336',
                            color: '#fff',
                            p: 0.7,
                            '&:hover': { bgcolor: '#d32f2f' },
                          }}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenEdit(table, e)}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.85)',
                          p: 0.7,
                          '&:hover': { bgcolor: '#fff', transform: 'scale(1.1)' },
                          transition: 'all 0.2s',
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteClick(table, e)}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.85)',
                          p: 0.7,
                          color: '#f44336',
                          '&:hover': { bgcolor: '#ffebee', transform: 'scale(1.1)' },
                          transition: 'all 0.2s',
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {isMergeMode && (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: `2.5px solid ${isSelected ? '#ff6b35' : '#ccc'}`,
                      bgcolor: isSelected ? '#ff6b35' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}
                  >
                    {isSelected && (
                      <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700 }}>✓</Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {table.capacity}
                  </Typography>
                </Box>
                {table.status === 'occupied' && table.current_order_id && (
                  <Chip
                    size="small"
                    icon={<ReceiptIcon sx={{ fontSize: 14 }} />}
                    label={`#${table.current_order_id}`}
                    sx={{
                      bgcolor: '#fff3e0',
                      color: '#ed6c02',
                      fontWeight: 600,
                      fontSize: '0.6rem',
                      height: 20,
                    }}
                  />
                )}
              </Box>

              {!isMergeMode && !isMobile && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  {table.status === 'empty' && (
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      startIcon={<ArrowIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTableClick(table);
                      }}
                      sx={{
                        bgcolor: '#ff6b35',
                        fontSize: '0.75rem',
                        py: 0.8,
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#e55a2b' },
                      }}
                    >
                      Start Order
                    </Button>
                  )}
                  
                  {(table.status === 'occupied' || table.status === 'billing') && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckoutIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFreeTable(table, e);
                        }}
                        sx={{
                          bgcolor: '#f44336',
                          fontSize: '0.75rem',
                          py: 0.8,
                          borderRadius: 2,
                          flex: 1,
                          '&:hover': { bgcolor: '#d32f2f' },
                        }}
                      >
                        Free
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/billing', { state: { tableId: table.id, tableNumber: table.table_number } });
                        }}
                        sx={{
                          fontSize: '0.75rem',
                          py: 0.8,
                          borderRadius: 2,
                          minWidth: 0,
                          px: 1.5,
                        }}
                      >
                        Bill
                      </Button>
                    </>
                  )}

                  {table.status === 'cleaning' && (
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      startIcon={<ReadyIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(table.id, 'empty');
                      }}
                      sx={{
                        bgcolor: '#4caf50',
                        fontSize: '0.75rem',
                        py: 0.8,
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#388e3c' },
                      }}
                    >
                      Ready
                    </Button>
                  )}

                  {table.status === 'reserved' && (
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      startIcon={<ArrowIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTableToCancelReserve(table);
                        setCancelReserveDialogOpen(true);
                      }}
                      sx={{
                        bgcolor: '#2196f3',
                        fontSize: '0.75rem',
                        py: 0.8,
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#1976d2' },
                      }}
                    >
                      Options
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>
      </Grid>
    );
  };

  // ==================== MAIN RENDER ====================
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2, fontWeight: 500, width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ 
        mb: { xs: 2, sm: 3 }, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        flexWrap: 'wrap', 
        gap: 2 
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 800, 
            color: '#1a1a2e', 
            mb: 0.5,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
          }}>
            Table Management
          </Typography>
          
          {/* ✅ FIXED: HTML nesting error fixed - Box instead of Typography */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <LayersIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
              {filteredTables.length} tables on {selectedFloor === 'all' ? 'all floors' : selectedFloor}
            </Typography>
            {!isElectron && (
              <Chip 
                size="small" 
                label="Browser Mode" 
                sx={{ 
                  ml: 1, 
                  bgcolor: '#e3f2fd', 
                  color: '#1976d2', 
                  fontSize: '0.6rem',
                  height: 20,
                }} 
              />
            )}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant={selectedTablesForMerge.length > 0 ? 'contained' : 'outlined'}
            color={selectedTablesForMerge.length > 0 ? 'primary' : 'inherit'}
            startIcon={<MergeIcon />}
            onClick={() => {
              if (selectedTablesForMerge.length > 0) {
                setMergeDialogOpen(true);
              } else {
                setSelectedTablesForMerge([]);
                showMessage('Select tables to merge by clicking them', 'info');
              }
            }}
            sx={{ 
              borderRadius: 2, 
              fontWeight: 600,
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            {selectedTablesForMerge.length > 0 ? `Merge (${selectedTablesForMerge.length})` : 'Merge'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon className={refreshing ? 'spin' : ''} />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ 
              borderRadius: 2,
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : 'Refresh'}
          </Button>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{
              bgcolor: '#ff6b35',
              '&:hover': { bgcolor: '#e55a2b' },
              fontWeight: 700,
              borderRadius: 2,
              px: { xs: 2, sm: 3 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Add' : 'Add Table'}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: { xs: 2, sm: 3 }, 
        borderRadius: 3, 
        display: 'flex', 
        gap: { xs: 1, sm: 2 }, 
        flexWrap: 'wrap', 
        alignItems: 'center',
        bgcolor: '#f8f9fa',
        border: '1px solid',
        borderColor: 'divider',
        overflowX: 'auto',
      }}>
        <Typography variant="subtitle2" sx={{ 
          fontWeight: 700, 
          color: '#1a1a2e', 
          mr: { xs: 0.5, sm: 1 },
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          whiteSpace: 'nowrap'
        }}>
          Status:
        </Typography>
        {Object.entries(TABLE_STATUS).map(([key, val]) => (
          <Chip
            key={key}
            size="small"
            icon={
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: val.color,
                  ml: 0.5,
                }}
              />
            }
            label={`${val.label} (${statusCounts[key] || 0})`}
            sx={{
              bgcolor: val.bg,
              color: val.color,
              fontWeight: 600,
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              border: `1px solid ${val.color}30`,
              '& .MuiChip-icon': { color: val.color },
              height: { xs: 28, sm: 32 },
            }}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          Total: {tables.length} Tables
        </Typography>
      </Paper>

      <Box sx={{ 
        mb: { xs: 2, sm: 3 }, 
        display: 'flex', 
        gap: 0.5, 
        flexWrap: 'wrap',
        overflowX: 'auto',
        pb: 1,
      }}>
        <ToggleButtonGroup
          value={selectedFloor}
          exclusive
          onChange={(e, val) => val && setSelectedFloor(val)}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            '& .MuiToggleButton-root': {
              borderRadius: '20px !important',
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 0.6, sm: 0.8 },
              textTransform: 'none',
              fontWeight: 600,
              border: '1px solid',
              borderColor: 'divider',
              mx: 0.3,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              whiteSpace: 'nowrap',
            },
            '& .Mui-selected': {
              bgcolor: '#1a1a2e !important',
              color: '#fff !important',
              borderColor: '#1a1a2e !important',
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          {FLOORS.map((floor) => (
            <ToggleButton key={floor} value={floor}>
              {floor}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Paper sx={{ 
        p: { xs: 1, sm: 1.5 }, 
        mb: { xs: 2, sm: 3 }, 
        borderRadius: 2, 
        bgcolor: '#f8f9fa',
        border: '1px solid',
        borderColor: 'divider',
      }}>
        <Typography variant="caption" sx={{ 
          color: 'text.secondary', 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1.5 }, 
          flexWrap: 'wrap',
          fontSize: { xs: '0.6rem', sm: '0.75rem' },
          justifyContent: { xs: 'flex-start', sm: 'center' },
        }}>
          <FreeIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#4caf50' }} /> 
          <strong>Empty</strong> = Start Order &nbsp;|&nbsp;
          <RestaurantIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#ff9800' }} /> 
          <strong>Occupied</strong> = Billing &nbsp;|&nbsp;
          <CleanIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#9c27b0' }} /> 
          <strong>Cleaning</strong> = Mark Ready &nbsp;|&nbsp;
          <Box sx={{ display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', gap: 0.5 }}>
            <FreeIcon sx={{ fontSize: 14, color: '#f44336' }} /> 
            <strong>Red Button</strong> = Checkout
          </Box>
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#ff6b35' }} />
        </Box>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
          {filteredTables.length > 0 ? (
            filteredTables.map(renderTableCard)
          ) : (
            <Grid item xs={12}>
              <Paper sx={{ 
                p: { xs: 4, sm: 6 }, 
                textAlign: 'center', 
                borderRadius: 4, 
                bgcolor: '#f8f9fa',
                border: '2px dashed',
                borderColor: 'divider',
              }}>
                <TableIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
                  No Tables Found
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
                  اس فلور پر کوئی ٹیبل نہیں
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAdd}
                  sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2 }}
                >
                  Add First Table
                </Button>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* ==================== DIALOGS ==================== */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#1a1a2e',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          {dialogMode === 'add' ? <AddIcon sx={{ color: '#ff6b35' }} /> : <EditIcon sx={{ color: '#ff6b35' }} />}
          {dialogMode === 'add' ? 'Add New Table' : 'Edit Table'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Table Number / ٹیبل نمبر"
            value={formData.table_number}
            onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
            margin="normal"
            placeholder="e.g., T-11, VIP-1"
            autoFocus
            required
            InputProps={{
              startAdornment: <TableIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Floor / فلور</InputLabel>
            <Select
              value={formData.floor}
              label="Floor / فلور"
              onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
            >
              {FLOORS.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Capacity / گنجائش"
            type="number"
            value={formData.capacity}
            onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
            margin="normal"
            inputProps={{ min: 1, max: 20 }}
            helperText="Number of persons this table can accommodate"
            InputProps={{
              startAdornment: <PeopleIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTable}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!formData.table_number.trim()}
            sx={{ 
              bgcolor: '#ff6b35', 
              '&:hover': { bgcolor: '#e55a2b' }, 
              borderRadius: 2,
              px: 3,
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mergeDialogOpen} onClose={() => { setMergeDialogOpen(false); setSelectedTablesForMerge([]); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 1 }}>
          <MergeIcon sx={{ color: '#ff6b35' }} /> Merge Tables
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Selected {selectedTablesForMerge.length} tables to merge:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {selectedTablesForMerge.map((t) => (
              <Chip
                key={t.id}
                label={t.table_number}
                avatar={<Avatar sx={{ bgcolor: TABLE_STATUS[t.status]?.color, width: 24, height: 24, fontSize: '0.7rem' }}>{t.table_number}</Avatar>}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Box>
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fff3e0' }}>
            <Typography variant="caption" sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningIcon fontSize="small" /> This will combine all selected tables into one order.
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => { setMergeDialogOpen(false); setSelectedTablesForMerge([]); }} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={handleMerge} variant="contained" startIcon={<MergeIcon />} disabled={selectedTablesForMerge.length < 2} sx={{ bgcolor: '#1a1a2e', borderRadius: 2, '&:hover': { bgcolor: '#2a2a4e' } }}>
            Confirm Merge
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon /> Delete Table?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{tableToDelete?.table_number}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" startIcon={<DeleteIcon />} sx={{ borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={freeTableDialogOpen} onClose={() => { if (!freeLoading) { setFreeTableDialogOpen(false); setTableToFree(null); } }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckoutIcon sx={{ color: '#f44336' }} /> Free Table
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {tableToFree && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1, textAlign: 'center', color: '#1a1a2e' }}>
                Table <strong style={{ color: '#ff6b35' }}>{tableToFree.table_number}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                Status: <Chip size="small" label={TABLE_STATUS[tableToFree.status]?.label || tableToFree.status} sx={{ bgcolor: TABLE_STATUS[tableToFree.status]?.bg, color: TABLE_STATUS[tableToFree.status]?.color }} />
              </Typography>
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#fff3e0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ed6c02', mb: 1 }}>
                  <WarningIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} /> Important
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Kya customer ne bill pay kar diya hai? Agar haan, to table ko free karein.
                </Typography>
              </Paper>
              <Stack spacing={1.5}>
                <Button fullWidth variant="contained" size="large" startIcon={freeLoading ? <CircularProgress size={20} color="inherit" /> : <CleanIcon />} onClick={() => handleCheckoutComplete(true)} disabled={freeLoading} sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' }, py: 1.5, borderRadius: 2 }}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="button" sx={{ fontWeight: 700, display: 'block' }}>Mark as Cleaning</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Table ko pehle saaf kiya jayega</Typography>
                  </Box>
                </Button>
                <Button fullWidth variant="outlined" size="large" startIcon={freeLoading ? <CircularProgress size={20} color="inherit" /> : <FreeIcon />} onClick={() => handleCheckoutComplete(false)} disabled={freeLoading} sx={{ borderColor: '#4caf50', color: '#4caf50', py: 1.5, borderRadius: 2, '&:hover': { bgcolor: '#e8f5e9', borderColor: '#4caf50' } }}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="button" sx={{ fontWeight: 700, display: 'block' }}>Direct Free</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Agar table pehle se saaf hai</Typography>
                  </Box>
                </Button>
                <Button fullWidth variant="text" onClick={() => { if (!freeLoading) { setFreeTableDialogOpen(false); setTableToFree(null); } }} disabled={freeLoading} sx={{ color: 'text.secondary' }}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={cancelReserveDialogOpen} onClose={() => { setCancelReserveDialogOpen(false); setTableToCancelReserve(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ color: '#2196f3' }} /> Reserved Table
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {tableToCancelReserve && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                Table <strong style={{ color: '#2196f3' }}>{tableToCancelReserve.table_number}</strong> is reserved.
              </Typography>
              <Stack spacing={1.5}>
                <Button fullWidth variant="contained" size="large" startIcon={<ArrowIcon />} onClick={handleStartFromReserved} sx={{ bgcolor: '#2196f3', '&:hover': { bgcolor: '#1976d2' }, py: 1.5, borderRadius: 2 }}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="button" sx={{ fontWeight: 700, display: 'block' }}>Start Order</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Reservation ko order mein convert karein</Typography>
                  </Box>
                </Button>
                <Button fullWidth variant="outlined" size="large" startIcon={<CancelIcon />} onClick={handleCancelReservation} sx={{ borderColor: '#f44336', color: '#f44336', py: 1.5, borderRadius: 2, '&:hover': { bgcolor: '#ffebee', borderColor: '#f44336' } }}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="button" sx={{ fontWeight: 700, display: 'block' }}>Cancel Reservation</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Table ko empty mark karein</Typography>
                  </Box>
                </Button>
                <Button fullWidth variant="text" onClick={() => { setCancelReserveDialogOpen(false); setTableToCancelReserve(null); }} sx={{ color: 'text.secondary' }}>
                  Close
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Table {actionTable?.table_number}</span>
          <IconButton onClick={() => setActionDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {actionTable && (
            <Stack spacing={1.5}>
              <Chip label={TABLE_STATUS[actionTable.status]?.label} sx={{ bgcolor: TABLE_STATUS[actionTable.status]?.bg, color: TABLE_STATUS[actionTable.status]?.color, fontWeight: 700 }} />
              {(actionTable.status === 'occupied' || actionTable.status === 'billing') && (
                <>
                  <Button fullWidth variant="contained" startIcon={<CheckoutIcon />} onClick={() => { setActionDialogOpen(false); handleFreeTable(actionTable); }} sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' }, py: 1.5, borderRadius: 2 }}>
                    Free Table
                  </Button>
                  <Button fullWidth variant="outlined" startIcon={<ReceiptIcon />} onClick={() => { setActionDialogOpen(false); navigate('/billing', { state: { tableId: actionTable.id, tableNumber: actionTable.table_number } }); }} sx={{ py: 1.5, borderRadius: 2 }}>
                    View Bill
                  </Button>
                </>
              )}
              {actionTable.status === 'reserved' && (
                <>
                  <Button fullWidth variant="contained" startIcon={<ArrowIcon />} onClick={() => { setActionDialogOpen(false); setTableToCancelReserve(actionTable); setCancelReserveDialogOpen(true); }} sx={{ bgcolor: '#2196f3', '&:hover': { bgcolor: '#1976d2' }, py: 1.5, borderRadius: 2 }}>
                    Start Order
                  </Button>
                  <Button fullWidth variant="outlined" startIcon={<CancelIcon />} onClick={() => { setActionDialogOpen(false); setTableToCancelReserve(actionTable); setCancelReserveDialogOpen(true); }} sx={{ borderColor: '#f44336', color: '#f44336', py: 1.5, borderRadius: 2 }}>
                    Cancel Reservation
                  </Button>
                </>
              )}
              {actionTable.status === 'cleaning' && (
                <Button fullWidth variant="contained" startIcon={<ReadyIcon />} onClick={() => { setActionDialogOpen(false); handleStatusChange(actionTable.id, 'empty'); }} sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' }, py: 1.5, borderRadius: 2 }}>
                  Mark Ready
                </Button>
              )}
              <Divider />
              <Button fullWidth variant="outlined" startIcon={<EditIcon />} onClick={() => { setActionDialogOpen(false); handleOpenEdit(actionTable); }} sx={{ py: 1.5, borderRadius: 2 }}>
                Edit Table
              </Button>
              <Button fullWidth variant="outlined" startIcon={<DeleteIcon />} onClick={() => { setActionDialogOpen(false); handleDeleteClick(actionTable); }} sx={{ borderColor: '#f44336', color: '#f44336', py: 1.5, borderRadius: 2 }}>
                Delete Table
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Zoom in={true}>
        <Fab
          color="primary"
          aria-label="add"
          onClick={handleOpenAdd}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            display: { xs: 'flex', md: 'none' },
            bgcolor: '#ff6b35',
            '&:hover': { bgcolor: '#e55a2b' },
            boxShadow: '0 8px 24px rgba(255,107,53,0.4)',
          }}
        >
          <AddIcon />
        </Fab>
      </Zoom>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default TableManagement;